<template>
    <component :is="rootListTag" v-if="items" role="menu" v-bind="rootListBindings">
        <template v-for="(item, i) of items">
            <component
                :is="rootItemTag"
                v-if="visible(item) && !item.separator && $temAcessoView(item.rules, item.requiredUnidade)"
                :key="item.label || i"
                :class="[{ 'layout-root-menuitem': root, 'active-menuitem': activeIndex === i && !item.disabled && !hasActiveChildInRoute(item) }]"
                role="none"
                v-bind="rootItemBindings"
            >
                <div
                    class="layout-menuitem-root-text"
                    :class="{ 'layout-menuitem-root-text--toggle': !!item.items }"
                    v-if="root"
                    role="button"
                    :tabindex="item.items ? 0 : -1"
                    :aria-expanded="item.items ? isRootItemOpen(i) : null"
                    @click="item.items && toggleRootItem(i)"
                    @keydown.enter.prevent="item.items && toggleRootItem(i)"
                    @keydown.space.prevent="item.items && toggleRootItem(i)"
                >
                    <span>{{ item.label }}</span>
                    <i
                        v-if="item.items"
                        class="pi pi-fw pi-angle-down layout-root-toggler"
                        :class="{ 'layout-root-toggler--open': isRootItemOpen(i) }"
                        aria-hidden="true"
                    ></i>
                </div>
                <router-link
                    v-if="item.to"
                    :to="item.to"
                    :style="item.style"
                    :class="[item.class, 'p-ripple', { 'p-disabled': item.disabled, 'active-route': isMenuRouteActive(item) }]"
                    active-class=""
                    exact-active-class=""
                    :target="item.target"
                    @click="onMenuItemClick($event, item, i)"
                    @mouseenter="onMenuItemMouseEnter(i)"
                    role="menuitem"
                    v-ripple
                >
                    <i :class="['layout-menuitem-icon', item.icon]" v-if="item.icon && item.icon.includes('pi-')"></i>
                    <font-awesome-icon :class="['layout-menuitem-icon mr-3', item.icon]" :icon="item.icon" v-if="item.icon && !item.icon.includes('pi-')" />
                    <span class="layout-menuitem-text">{{ item.label }}</span>
                    <i v-if="item.items" class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
                    <Badge v-if="item.badge" :value="item.badge"></Badge>
                </router-link>
                <a
                    v-if="!item.to"
                    :href="item.url || '#'"
                    :style="item.style"
                    :class="[item.class, 'p-ripple', { 'p-disabled': item.disabled }]"
                    :target="item.target"
                    @click="onMenuItemClick($event, item, i)"
                    @mouseenter="onMenuItemMouseEnter(i)"
                    v-ripple
                >
                    <i :class="['layout-menuitem-icon', item.icon]" v-if="item.icon && item.icon.includes('pi-')"></i>
                    <font-awesome-icon :class="['layout-menuitem-icon mr-3', item.icon]" :icon="item.icon" v-if="item.icon && !item.icon.includes('pi-')" />
                    <span class="layout-menuitem-text">{{ item.label }}</span>
                    <i v-if="item.items" class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
                    <Badge v-if="item.badge" :value="item.badge"></Badge>
                </a>
                <div class="layout-menu-tooltip">
                    <div class="layout-menu-tooltip-arrow"></div>
                    <div class="layout-menu-tooltip-text">{{ item.label }}</div>
                </div>
                <div v-show="item.items && (root ? isRootItemOpen(i) : activeIndex === i)" class="layout-submenu-panel">
                    <appsubmenu
                        :ref="(el) => setSubmenuRef(i, el)"
                        :items="visible(item) && item.items"
                        :menuActive="menuActive"
                        :layoutMode="layoutMode"
                        :parentMenuItemActive="activeIndex === i"
                        @menuitem-click="$emit('menuitem-click', $event)"
                    ></appsubmenu>
                </div>
            </component>
        </template>
    </component>
</template>
<script>
import { motion } from 'motion-v';
import EventBus from './event-bus';
import { sidebarMenuVariants, sidebarMenuItemVariants } from '@packages/design-system';
import { animateSubmenuOpen } from '@packages/design-system';

export default {
    name: 'appsubmenu',
    emits: ['root-menuitem-click', 'menuitem-click'],
    components: {
        MotionUl: motion.ul,
        MotionLi: motion.li,
    },
    props: {
        items: Array,
        layoutMode: String,
        menuActive: Boolean,
        mobileMenuActive: Boolean,
        root: {
            type: Boolean,
            default: false,
        },
        parentMenuItemActive: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            activeIndex: null,
            rootOpenState: {},
            submenuRefs: {},
            sidebarMenuVariants,
            sidebarMenuItemVariants,
        };
    },
    computed: {
        rootListTag() {
            return this.root ? 'MotionUl' : 'ul';
        },
        rootItemTag() {
            return this.root ? 'MotionLi' : 'li';
        },
        rootListBindings() {
            if (!this.root) {
                return {};
            }
            return {
                variants: this.sidebarMenuVariants,
                initial: 'hidden',
                animate: 'visible',
            };
        },
        rootItemBindings() {
            if (!this.root) {
                return {};
            }
            return {
                variants: this.sidebarMenuItemVariants,
            };
        },
    },
    created() {
        this.syncRootOpenState(true);
    },
    watch: {
        '$route.path'() {
            this.syncRootOpenState();
        },
    },
    mounted() {
        EventBus.on('reset-active-index', () => {
            if (this.layoutMode === 'horizontal' || this.layoutMode === 'slim') {
                this.activeIndex = null;
            }
        });
    },
    methods: {
        setSubmenuRef(index, el) {
            if (el) {
                this.submenuRefs[index] = el;
            } else {
                delete this.submenuRefs[index];
            }
        },
        isRootItemOpen(i) {
            return this.rootOpenState[i] !== false;
        },
        toggleRootItem(i) {
            const opening = !this.isRootItemOpen(i);
            this.rootOpenState[i] = opening;

            if (opening) {
                this.$nextTick(() => animateSubmenuOpen(this.submenuRefs[i]));
            }
        },
        syncRootOpenState(initial = false) {
            if (!this.root || !Array.isArray(this.items)) {
                return;
            }

            this.items.forEach((item, i) => {
                if (!item.items) {
                    return;
                }

                if (this.containsActiveRoute(item)) {
                    this.rootOpenState[i] = true;
                } else if (initial) {
                    this.rootOpenState[i] = false;
                }
            });
        },
        containsActiveRoute(item) {
            if (this.isMenuRouteActive(item)) {
                return true;
            }
            if (!item?.items?.length) {
                return false;
            }
            return item.items.some((child) => this.containsActiveRoute(child));
        },
        isMenuRouteActive(item) {
            if (!item?.to) {
                return false;
            }
            const path = this.$route.path;
            const to = item.to;
            if (item.matchChildren) {
                return path === to || path.startsWith(`${to}/`);
            }
            return path === to;
        },
        hasActiveChildInRoute(item) {
            if (!item?.items?.length) {
                return false;
            }
            return item.items.some((child) => this.isMenuRouteActive(child));
        },
        onMenuItemClick(event, item, index) {
            if (item.disabled) {
                event.preventDefault();
                return;
            }

            if (item.command) {
                item.command({ originalEvent: event, item: item });
                event.preventDefault();
            }

            if (item.items) {
                event.preventDefault();
            }

            if (this.root) {
                this.$emit('root-menuitem-click', {
                    originalEvent: event,
                });
            }

            if (item.items) {
                const wasOpen = this.activeIndex === index;
                this.activeIndex = wasOpen ? null : index;
                if (!wasOpen) {
                    this.$nextTick(() => animateSubmenuOpen(this.submenuRefs[index]));
                }
            } else {
                if (this.layoutMode !== 'static') {
                    const ink = this.getInk(event.currentTarget);
                    if (ink) {
                        this.removeClass(ink, 'p-ink-active');
                    }
                }
            }

            this.$emit('menuitem-click', {
                originalEvent: event,
                item: item,
            });
        },
        isMobile() {
            return window.innerWidth <= 1025;
        },
        onMenuItemMouseEnter(index) {
            if (this.root && this.menuActive && (this.layoutMode === 'horizontal' || this.layoutMode === 'slim') && !this.isMobile()) {
                this.activeIndex = index;
            }
        },
        visible(item) {
            return typeof item.visible === 'function' ? item.visible() : item.visible !== false;
        },
        isSlim() {
            return this.layoutMode === 'slim';
        },
        isHorizontal() {
            return this.layoutMode === 'horizontal';
        },
        getInk(el) {
            for (let i = 0; i < el.children.length; i++) {
                if (typeof el.children[i].className === 'string' && el.children[i].className.indexOf('p-ink') !== -1) {
                    return el.children[i];
                }
            }
            return null;
        },
        removeClass(element, className) {
            if (element.classList) element.classList.remove(className);
            else element.className = element.className.replace(new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        },
    },
};
</script>
